import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  Button,
  Skeleton,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";

import { VISIT_COLORS, HEALTH_STATUS_COLORS, SP_VISIT_COLORS, getSpVisitAge } from "../../constants/clinicalDashboard";
import ContactDialog from "../CareManager/ContactDialog";

const ROWS_PER_PAGE = 25;

/**
 * Tabla de pacientes para recall (sin visita reciente).
 * Columnas: Patient, Since, Last Visit, Status, Gaps, Phone, Email, Provider, Action.
 */
export default function RecallTable({ recallList, filters, loading, onCreateTask }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactPatient, setContactPatient] = useState(null);

  const handleOpenContact = (patient) => {
    setContactPatient(patient);
    setContactDialogOpen(true);
  };

  // Reset page when filters change
  React.useEffect(() => { setPage(0); }, [filters]);

  // Filter recall list
  const filtered = useMemo(() => {
    const list = recallList || [];
    if (!filters) return list;

    return list.filter((p) => {
      if (filters.search) {
        const q = filters.search.toLowerCase();
        if (!p.name?.toLowerCase().includes(q) && !String(p.cipId).includes(q)) return false;
      }
      if (filters.healthStatus && filters.healthStatus !== "All" && p.healthStatus !== filters.healthStatus) return false;
      if (filters.since && filters.since !== "All" && p.timeSinceLastVisit !== filters.since) return false;
      return true;
    });
  }, [recallList, filters]);

  if (loading) {
    return (
      <Stack spacing={1}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={40} />
        ))}
      </Stack>
    );
  }

  const paged = filtered.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);

  return (
    <>
      <TableContainer sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {["Patient", "Since", "Status", "Gaps", "SP Visit", "Obs", "Tasks", "Contact", "Provider", ""].map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.68rem",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    color: "text.secondary",
                    py: 0.75,
                    whiteSpace: "nowrap",
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No recall patients
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paged.map((p) => {
                const visitColors = VISIT_COLORS[p.timeSinceLastVisit] || {};
                const statusColors = HEALTH_STATUS_COLORS[p.healthStatus] || HEALTH_STATUS_COLORS["No Status"];
                return (
                  <TableRow key={p.cipId} hover sx={{ "&:hover": { bgcolor: "#F8FAFB" } }}>
                    {/* Patient */}
                    <TableCell sx={{ py: 0.75 }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          fontSize: "0.76rem",
                          cursor: "pointer",
                          "&:hover": { color: "#2563EB", textDecoration: "underline" },
                        }}
                        onClick={() => navigate(`/ClinicalInfo?cipId=${p.cipId}`)}
                      >
                        {p.name}
                      </Typography>
                    </TableCell>

                    {/* Since */}
                    <TableCell sx={{ py: 0.75 }}>
                      <Chip
                        label={p.timeSinceLastVisit}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.64rem",
                          fontWeight: 600,
                          bgcolor: visitColors.bg || "#F1F5F9",
                          color: visitColors.text || "#475569",
                        }}
                      />
                    </TableCell>

                    {/* Status */}
                    <TableCell sx={{ py: 0.75 }}>
                      <Chip
                        label={p.healthStatus || "—"}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.64rem",
                          fontWeight: 600,
                          bgcolor: statusColors.bg,
                          color: statusColors.text,
                        }}
                      />
                    </TableCell>

                    {/* Gaps */}
                    <TableCell align="center" sx={{ py: 0.75 }}>
                      <Typography
                        variant="caption"
                        fontWeight={(p.openGaps || 0) > 0 ? 700 : 400}
                        sx={{
                          fontSize: "0.74rem",
                          color: (p.openGaps || 0) >= 3 ? "#DC2626" : (p.openGaps || 0) >= 1 ? "#D97706" : "text.secondary",
                        }}
                      >
                        {p.openGaps || 0}
                      </Typography>
                    </TableCell>

                    {/* SP Visit */}
                    <TableCell align="center" sx={{ py: 0.75 }}>
                      <RecallSpVisitChip date={p.lastSantePlusVisit} />
                    </TableCell>

                    {/* Observations */}
                    <TableCell align="center" sx={{ py: 0.75 }}>
                      <RecallCountBadge count={p.observationsCount} color="#2563EB" bgColor="#DBEAFE" />
                    </TableCell>

                    {/* Active Tasks */}
                    <TableCell align="center" sx={{ py: 0.75 }}>
                      <RecallCountBadge count={p.activeTasks} color="#D97706" bgColor="#FEF3C7" />
                    </TableCell>

                    {/* Contact */}
                    <TableCell sx={{ py: 0.75 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ContactPhoneIcon sx={{ fontSize: 14 }} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenContact(p);
                        }}
                        sx={{ textTransform: "none", fontSize: "0.68rem", py: 0.25 }}
                      >
                        Contact
                      </Button>
                    </TableCell>

                    {/* Provider */}
                    <TableCell sx={{ py: 0.75 }}>
                      <Typography variant="caption" sx={{ fontSize: "0.7rem" }} noWrap>
                        {p.pcpName || "—"}
                      </Typography>
                    </TableCell>

                    {/* Action */}
                    <TableCell sx={{ py: 0.75 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip title="View Patient">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/patient-info/${p.cipId}`)}
                            sx={{ color: "#6366F1" }}
                          >
                            <VisibilityIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => onCreateTask?.(p)}
                          sx={{ fontSize: "0.68rem", textTransform: "none", minWidth: 0, px: 0.5 }}
                        >
                          Create Task
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {filtered.length > ROWS_PER_PAGE && (
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[ROWS_PER_PAGE]}
          sx={{ "& .MuiTablePagination-displayedRows": { fontSize: "0.72rem" } }}
        />
      )}

      <ContactDialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        patient={contactPatient}
      />
    </>
  );
}

// ─── Sub-components ─────────────────────────────────────────────

function RecallSpVisitChip({ date }) {
  const age = getSpVisitAge(date);
  const colors = SP_VISIT_COLORS[age];

  if (!date) {
    return (
      <Chip
        label="—"
        size="small"
        sx={{ height: 18, fontSize: "0.62rem", fontWeight: 600, bgcolor: colors.bg, color: colors.text }}
      />
    );
  }

  const d = new Date(date);
  const label = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

  return (
    <Chip
      label={label}
      size="small"
      sx={{ height: 18, fontSize: "0.62rem", fontWeight: 600, bgcolor: colors.bg, color: colors.text }}
    />
  );
}

function RecallCountBadge({ count, color, bgColor }) {
  const val = count || 0;
  if (val === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
        0
      </Typography>
    );
  }
  return (
    <Chip
      label={val}
      size="small"
      sx={{ height: 18, minWidth: 22, fontSize: "0.64rem", fontWeight: 700, bgcolor: bgColor, color }}
    />
  );
}
